import { SourceSpecification, LayerSpecification, LngLatBoundsLike,DataDrivenPropertyValueSpecification,ExpressionInputType, FilterSpecification } from 'maplibre-gl';
import { function as styleFunction } from '@mapbox/mapbox-gl-style-spec';

import { Map } from './Map';

import expression from "./Expression";

import {
  layerSource,
  property,
  layerType,
  colorType,
  fields,
  expressionType,
  subLayerType,
  propertyValue,
  layerSourceType
} from './Types';

import * as Utils from './utils';

import layout from './const/layout.json';

import defaults from './const/default.json';

export const LAYER_MIN_ZOOM: number = 0;
export const LAYER_MAX_ZOOM: number = 24;

export class Layer {
  public source: layerSource;

  public mapObj: Map;

  public allFields: fields = [];

  public publishedFields: fields = [];

  public prefix: string;

  public sourceId: string;

  public layerId: string;

  public sourceLayer: string;

  public colorType?: colorType;

  public sourceType?: layerSourceType;

  public layerType: string;

  constructor(
    layerSource: layerSource,
    mapObj: Map,
    sourceLayerId?: string,
    layerId?: string,
    sourceId?: string
  ) {
    this.source = layerSource;
    this.mapObj = mapObj;
    this.prefix = layerSource.layer_type;

    this.sourceId = sourceId || `geobox://${this.prefix}/${this.source.id}`;
    this.layerId = layerId || layerSource.name || `${this.prefix}/${this.source.name}`;
    this.sourceLayer = sourceLayerId || this.source.name;

    this.layerType = this.source.layer_type;
    
  }

  zoomToLayer = async (options?: object) => {
    options = options || { maxZoom: 22 };

    await this.mapObj.fitBounds(
      <LngLatBoundsLike>[
        Utils.webMercatorToLngLat(this.source.extent[0], this.source.extent[1]),
        Utils.webMercatorToLngLat(this.source.extent[2], this.source.extent[3])
      ],
      options
    );
  };

  spatialFilter = async (options: {
    wkt: string;
    srid?: number;
    spatial_predicate?: string;
  }): Promise<unknown> => {
    const source = this.source as {
      spatialFilter?: (opts: {
        wkt: string;
        srid?: number;
        spatial_predicate?: string;
      }) => Promise<unknown>;
    };
    const direct = source?.spatialFilter;
    const proto =
      source != null
        ? (Object.getPrototypeOf(source) as { spatialFilter?: typeof direct } | null)
            ?.spatialFilter
        : undefined;
    const fn = typeof direct === 'function' ? direct : proto;
    if (typeof fn !== 'function') {
      throw new Error('spatialFilter is not supported on this layer source');
    }
    return fn.call(source, {
      wkt: options.wkt,
      srid: options.srid ?? 3857,
      spatial_predicate: options.spatial_predicate ?? 'Intersects',
    });
  };

  filterByLayer = async (options: {
    select_layer_uuid: string;
    spatial_predicate?: string;
    feature_ids?: (number | string)[] | null;
  }): Promise<unknown> => {
    const source = this.source as {
      filterByLayer?: (opts: {
        select_layer_uuid: string;
        spatial_predicate?: string;
        feature_ids?: (number | string)[] | null;
      }) => Promise<unknown>;
    };
    const direct = source?.filterByLayer;
    const proto =
      source != null
        ? (Object.getPrototypeOf(source) as { filterByLayer?: typeof direct } | null)
            ?.filterByLayer
        : undefined;
    const fn = typeof direct === 'function' ? direct : proto;
    if (typeof fn !== 'function') {
      throw new Error('filterByLayer is not supported on this layer source');
    }
    return fn.call(source, {
      select_layer_uuid: options.select_layer_uuid,
      spatial_predicate: options.spatial_predicate ?? 'Intersects',
      feature_ids: options.feature_ids ?? null,
    });
  };

  // get visibility() {
  //   return (
  //     this.mapObj.getLayoutProperty(this.layerId, 'visibility') || 'visible'
  //   );
  // }

  getLayerIndex = (): number => {
    let index: number;

    const { layers } = this.mapObj.getStyle();

    for (index = 0; index < layers.length; index++) {
      if (layers[index].id === this.layerId) break;
    }

    return index;
  };

  setProperty= (type: property, value: any) => {
    return this._setLayerProperty(type, value, 'main');
  }
  
  _setLayerProperty = (type: property, value: any, subLayerType:subLayerType, method: string = 'count') => {
    try {

      let layerId = `${this.layerId}$${subLayerType}`;
      if (subLayerType === 'main') layerId = this.layerId;

      if (type === 'maxzoom') { 
        this.mapObj.setLayerZoomRange(
          layerId,
          this.mapObj.getLayer(layerId)?.minzoom || 0,
          <number>value
        );

        if (subLayerType === 'main'){
          if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$symbol`,
              this.mapObj.getLayer(`${this.layerId}$symbol`)?.minzoom || 0,
              <number>value
            );
          }
  
          if (this.mapObj.getLayer(`${this.layerId}$label`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$label`,
              this.mapObj.getLayer(`${this.layerId}$label`)?.minzoom || 0,
              <number>value
            );
          }
          
          if (this.mapObj.getLayer(`${this.layerId}$outline`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$outline`,
              this.mapObj.getLayer(`${this.layerId}$outline`)?.minzoom || 0,
              <number>value
            );
  
          }
  
          if (this.mapObj.getLayer(`${this.layerId}$extrusion`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$extrusion`,
              this.mapObj.getLayer(`${this.layerId}$extrusion`)?.minzoom || 0,
              <number>value
            );
  
          }
  
          if (this.mapObj.getLayer(`${this.layerId}$heatmap`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$heatmap`,
              this.mapObj.getLayer(`${this.layerId}$heatmap`)?.minzoom || 0,
              <number>value
            );
          }
  
          if (this.mapObj.getLayer(`${this.layerId}$cluster`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$cluster`,
              this.mapObj.getLayer(`${this.layerId}$cluster`)?.minzoom || 0,
              <number>value
            );
          }
  
          
          if (this.mapObj.getLayer(`${this.layerId}$unclustered`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$unclustered`,
              this.mapObj.getLayer(`${this.layerId}$unclustered`)?.minzoom || 0,
              <number>value
            );
          }
  
          if (this.mapObj.getLayer(`${this.layerId}$cluster-count`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$cluster-count`,
              this.mapObj.getLayer(`${this.layerId}$cluster-count`)?.minzoom || 0,
              <number>value
            );
          }
        }

      } else if (type === 'minzoom') {
        this.mapObj.setLayerZoomRange(
          layerId,
          <number>value,
          this.mapObj.getLayer(layerId)?.maxzoom || 24
        );

        if (subLayerType === 'main'){
          if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$symbol`,
              <number>value,
              this.mapObj.getLayer(`${this.layerId}$symbol`)?.maxzoom || 24
            );
          }

          if (this.mapObj.getLayer(`${this.layerId}$label`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$label`,
              <number>value,
              this.mapObj.getLayer(`${this.layerId}$label`)?.maxzoom || 24
            );
          }

          if (this.mapObj.getLayer(`${this.layerId}$outline`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$outline`,
              <number>value,
              this.mapObj.getLayer(`${this.layerId}$outline`)?.maxzoom || 24
            );

          }

          if (this.mapObj.getLayer(`${this.layerId}$extrusion`)) {
            this.mapObj.setLayerZoomRange(
              `${this.layerId}$extrusion`,
              <number>value,
              this.mapObj.getLayer(`${this.layerId}$extrusion`)?.maxzoom || 24
            );
          }
        }

       
      } else if (subLayerType === 'cluster' && (type === 'weight-field' || type === 'cluster-radius' || type === 'cluster-maxZoom')) {
        let style = this.mapObj.getStyle();
        let layerSource = <any> style.sources[`client_${this.sourceId}$_cluster`];

        if (type === 'weight-field'){
         
          if (method.toString().toLowerCase() === 'count'){
            layerSource["clusterProperties"]= {"cluster_value": ["+", 1]}

          } else {
            if (method.toString().toLowerCase() === 'avg'){
              layerSource["clusterProperties"]={
                "cluster_value": [
                    '/',
                    ['+', ['accumulated'], ['get', value]],
                    ['+', ['accumulated'], 1]
                ]
              }
            }else if (method.toString().toLowerCase() === 'max'){
              layerSource["clusterProperties"]={
                "cluster_value": ['max', ['get', value]]
              }
            }else if (method.toString().toLowerCase() === 'min'){
              layerSource["clusterProperties"]={
                "cluster_value": ['min', ['get', value]]
              }
            }else {
              layerSource["clusterProperties"]={
                "cluster_value": ["+", ["get", value]]
              }
            }
          }
    
        }else if (type === 'cluster-radius'){
          layerSource["clusterRadius"]= value;
        }else if (type === 'cluster-maxZoom'){
          layerSource["clusterMaxZoom"]= value;
        }
        
        style.sources[`${this.sourceId}$_cluster`] = <SourceSpecification>layerSource;
  
        this.mapObj.setStyle(style)

      } else if (type === 'highlight-color') {
        let paintProperty: Array<string | string[]> = <
          Array<string | string[]>
        >this.mapObj.getPaintProperty(this.layerId, <string>this.colorType);

        if (paintProperty[0] === 'case') {
          paintProperty[4] = <string>value;
        } else if (paintProperty[0] === 'interpolate') {
          for (let i = 4; i < paintProperty.length; i += 2) {
            (<Array<string[]>>paintProperty)[i][4] = <string>value;
          }
        }

        this.mapObj.setPaintProperty(
          this.layerId,
          <string>this.colorType,
          paintProperty
        );

        if (this.colorType === 'fill-color') {
          paintProperty = <Array<string | string[]>>(
            this.mapObj.getPaintProperty(this.layerId, 'fill-outline-color')
          );

          if (paintProperty[0] === 'case') {
            paintProperty[2] = <string>value;
          } else if (paintProperty[0] === 'interpolate') {
            for (let i = 4; i < paintProperty.length; i += 2) {
              (<Array<string[]>>paintProperty)[i][2] = <string>value;
            }
          }
          this.mapObj.setPaintProperty(
            this.layerId,
            'fill-outline-color',
            paintProperty
          );
        }
      } else if (type === 'select-color') {
        let paintProperty: Array<string | string[]> = <
          Array<string | string[]>
        >this.mapObj.getPaintProperty(this.layerId, <string>this.colorType);
        if (paintProperty[0] === 'case') {
          paintProperty[2] = <string>value;
        } else if (paintProperty[0] === 'interpolate') {
          for (let i = 4; i < paintProperty.length; i += 2) {
            (<Array<string[]>>paintProperty)[i][2] = <string>value;
          }
        }
        this.mapObj.setPaintProperty(
          this.layerId,
          <string>this.colorType,
          paintProperty
        );

      } else if (['line-color','fill-color','fill-extrusion-color','icon-color','circle-color'].includes(type)) {
        let paintProperty: Array<string | string[]> 
       
        paintProperty = value;

        if (subLayerType === 'cluster' || subLayerType === 'unclustered'){
          this.mapObj.setPaintProperty(
            layerId,
            type,
            paintProperty
          );
        }else {
          paintProperty = this.prepareNewStyle(paintProperty);

          this.mapObj.setPaintProperty(
            layerId,
            type,
            paintProperty
          );
        }

      } else if (['fill-opacity','fill-outline-color'].includes(type)) {
        let paintProperty: Array<string | string[]> 

        paintProperty = value;
        // if (type === 'fill-outline-color'){
        //   paintProperty = this.prepareNewStyle(paintProperty, false);
        // }else {
          paintProperty = this.prepareNewStyle(paintProperty, true);
        // }

        this.mapObj.setPaintProperty(
          layerId,
          type,
          paintProperty
        );

      } else {
        let layerType: layerType = 'line';

        if (this.mapObj.getLayer(layerId)) layerType = <layerType>this.mapObj.getLayer(layerId)?.type;

        let group = 'paint';

        if (layout[layerType].paint.includes(type)) {
          group = 'paint';
        } else {
          group = 'layout';
        }

        if (layerType === 'symbol') {
          for (const field of this.allFields) {
            if (field.name === value) {
              value = `{${value}}`;
              break;
            }
          }
        }

        if (group === 'paint') {

          this.mapObj.setPaintProperty(layerId, type, value);
     
        // } else if (type === 'visibility') {

        //   this.mapObj.setLayoutProperty(layerId, type, value);
            
          // if (this.mapObj.getLayer(`${this.layerId}$heatmap`)) {
          //   this.mapObj.setLayoutProperty(
          //     `${this.layerId}$heatmap`,
          //     type,
          //     value
          //   );
          // }else if (this.mapObj.getLayer(`${this.layerId}$cluster`)) {
          //   this.mapObj.setLayoutProperty(
          //     `${this.layerId}$cluster`,
          //     type,
          //     value
          //   );

          //   this.mapObj.setLayoutProperty(
          //     `${this.layerId}$unclustered`,
          //     type,
          //     value
          //   );

          //   this.mapObj.setLayoutProperty(
          //     `${this.layerId}$cluster-count`,
          //     type,
          //     value
          //   );
          // }else {
            
          // }
          
          // if (this.mapObj.getLayer(`${this.layerId}$symbol`)) {
          //   this.mapObj.setLayoutProperty(
          //     `${this.layerId}$symbol`,
          //     type,
          //     value
          //   );
          // }

          // if (this.mapObj.getLayer(`${this.layerId}$outline`)) {
          //   this.mapObj.setLayoutProperty(
          //     `${this.layerId}$outline`,
          //     type,
          //     value
          //   );

          // }

        } else {
          
          if (type === 'visibility' && (this.mapObj.getLayer(`${this.layerId}$cluster`) ||this.mapObj.getLayer(`${this.layerId}$heatmap`) || this.mapObj.getLayer(`${this.layerId}$hillshade`) || this.mapObj.getLayer(`${this.layerId}$raster`) )){
            if (this.mapObj.getLayer(`${this.layerId}$hillshade`)){
              this.mapObj.setLayoutProperty(`${this.layerId}$hillshade`, type, value);
            }else if (this.mapObj.getLayer(`${this.layerId}$raster`)){
              this.mapObj.setLayoutProperty(`${this.layerId}$raster`, type, value);
            }else if (this.mapObj.getLayer(`${this.layerId}$heatmap`)){
              this.mapObj.setLayoutProperty(`${this.layerId}$heatmap`, type, value);
            }else if (this.mapObj.getLayer(`${this.layerId}$cluster`)){
              this.mapObj.setLayoutProperty(`${this.layerId}$cluster`, type, value);
              this.mapObj.setLayoutProperty(`${this.layerId}$unclustered`, type, value);
              this.mapObj.setLayoutProperty(`${this.layerId}$cluster-count`, type, value);
            }
            
          }else{
            
            this.mapObj.setLayoutProperty(layerId, type, value);
          }
          
        }
      }
    } catch (error) {
      
      
    }
  };

  getProperty = (type: property) => {
    return <propertyValue>this._getLayerProperty(type, 'main');
  }

  _getLayerProperty = (type: property, subLayerType: subLayerType) => {

    try {

      let layerId = `${this.layerId}$${subLayerType}`;
      if (subLayerType === 'main') layerId = this.layerId;

      let value;

      let paintProperty: any;

      if (type === 'maxzoom') {
        value = {"basic": this.mapObj.getLayer(layerId)?.maxzoom || defaults['maxzoom']};
      } else if (type === 'minzoom') {
        value = {"basic":this.mapObj.getLayer(layerId)?.minzoom || defaults['minzoom']};
      } else if (type === 'highlight-color') {
       
        paintProperty = (
          this.mapObj.getPaintProperty(this.layerId, <string>this.colorType)
        );
        if (paintProperty && (["string", "number", "boolean"].includes(typeof paintProperty))){
          value = {"basic": paintProperty};
        } else if (paintProperty && paintProperty[0] === 'case') {
          if ((["string", "number", "boolean"].includes(typeof paintProperty[4])) ) {
            value = {"basic": paintProperty[4]};
          }else {
            value = {"case": paintProperty[4]};
          }
        } else if (paintProperty && paintProperty[0] === 'match') {
          value = {"match": paintProperty};
        } else if (paintProperty && paintProperty[0] === 'interpolate') {
          value = {"interpolate": paintProperty[4][4]};
        }
      } else if (type === 'select-color') {
        paintProperty = (
          this.mapObj.getPaintProperty(this.layerId, <string>this.colorType)
        );
        if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)))){
          value = {"basic": paintProperty};
        } else if (paintProperty && paintProperty[0] === 'case') {
          if ((["string", "number", "boolean"].includes(typeof paintProperty[2]))) {
            value = {"basic": paintProperty[2]};
          }else {
            value = {"case": paintProperty[2]};
          }
        } else if (paintProperty && paintProperty[0] === 'match') {
          value = {"match": paintProperty};
        } else if (paintProperty && paintProperty[0] === 'interpolate') {
          value = {"interpolate": paintProperty[4][2]};
        }

      } else if (type === 'fill-opacity' || type === 'fill-outline-color') {
        paintProperty = (
          this.mapObj.getPaintProperty(layerId, type)
        );
  
        let base = undefined;
        try {
          if (paintProperty && paintProperty.base){
            base = paintProperty.base;
            delete paintProperty.base;
          }
        } catch (error) {
          // throw new Error(String(error));
        }

        if (typeof paintProperty !== "boolean" && !paintProperty){
          value = {"basic": defaults[type]};
        }else if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)))){
          value = {"basic": paintProperty};
        }else if (paintProperty && paintProperty[3][0] === 'get') {
          value = {"basic": paintProperty[3]};
        }else if (paintProperty && paintProperty[3][0] === 'match') {
          value = {"match": paintProperty[3]};
        } else if (paintProperty && paintProperty[0] === 'case') {
          if (["string", "number", "boolean"].includes(typeof paintProperty[3])) {
            value = {"basic": paintProperty[3]};
          }else {
            try {
              value = {"case": paintProperty[3],"field":paintProperty[3][1][1][1]};
            } catch (error) {
              value = {"case": paintProperty[3]};
            }
            
          }
          
        } else if (paintProperty && paintProperty[0] === 'interpolate') {
          let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
          
          if (propertyObject?.inputs.length === 1){
            if ((["string", "number", "boolean"].includes(typeof paintProperty[3])) ) {
              value = {"basic": paintProperty[4][3]};
            }else {
              try {
                value = {"case": paintProperty[4][3],"field":paintProperty[4][3][1][1][1]};
              } catch (error) {
                value = {"case": paintProperty[4][3]};
              }
            }
            
          }else {
            for (let index = 0; index < <number>propertyObject?.outputs.length; index++) {
             
              propertyObject.outputs[index] = propertyObject?.outputs[index][3];
              propertyObject.stops[index][1] = propertyObject.stops[index][1][3];
            }
            value = {"interpolate": propertyObject.stops, "field":propertyObject.fieldName};
            if (base){
              (<any>value)["base"] = base;
            }
          }
          
        }else {
          value = {"basic": paintProperty};
        }
      } else if (['line-color', 'fill-color', 'circle-color','icon-color','fill-extrusion-color'].includes(type)) {
        paintProperty = (this.mapObj.getPaintProperty(layerId, type));

        let base = undefined;
        try {
          if (paintProperty && paintProperty.base){
            base = paintProperty.base;
            delete paintProperty.base;
          }
        } catch (error) {
          // throw new Error(String(error));
        }
        
        if (typeof paintProperty !== "boolean" && !paintProperty){
          value = {"basic": defaults[type]};
        } else if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)) )){
          value = {"basic": paintProperty};
        } else if (paintProperty && paintProperty[5][0] === 'get') {
          value = {"basic": paintProperty[5]};
        } else if (paintProperty && paintProperty[5][0] === 'match') {
          value = {"match": paintProperty[5]};
        } else if (paintProperty && paintProperty[0] === 'step') {
          value = {"step": paintProperty};
        } else if (paintProperty && paintProperty[0] === 'case') {
          if ( (["string", "number", "boolean"].includes(typeof paintProperty[5]))) {
            value = {"basic": paintProperty[5]};
          } else {
            try {
              value = {"case": paintProperty[5],"field":paintProperty[5][1][1][1]};
            } catch (error) {
              value = {"case": paintProperty[5]};
            }
          }
        } else if (paintProperty && paintProperty[0] === 'interpolate') {
          let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
         
          if (propertyObject?.inputs.length === 1){
            if ( (["string", "number", "boolean"].includes(typeof paintProperty[5])) ) {
              value = {"basic": paintProperty[4][3]};
            }else {
              try {
                value = {"case": paintProperty[4][3],"field":paintProperty[4][5][1][1][1]};
              } catch (error) {
                value = {"case": paintProperty[4][3]};
              }
            }
            
          } else {
            for (let index = 0; index < <number>propertyObject?.outputs.length; index++) {
              propertyObject.outputs[index] = propertyObject?.outputs[index][5];
              propertyObject.stops[index][1] = propertyObject.stops[index][1][5];
            }
            value = {"interpolate": propertyObject.stops, "field":propertyObject.fieldName};
            if (base){
              (<any>value)["base"] = base;
            }
          }
          
        } else {
          value =  value = {"basic": paintProperty};
        }
      } else {
    
        let layerType: layerType = 'line';
        if (this.mapObj.getLayer(layerId))
          layerType = <layerType>this.mapObj.getLayer(layerId)?.type;

        let group = 'paint';
      
        if (layout[layerType].paint.includes(type)) {
          group = 'paint';
        } else {
          group = 'layout';
        }

        if (group === 'paint') {
          // value = this.mapObj.getPaintProperty(layerid, type);
          paintProperty = this.mapObj.getPaintProperty(layerId, type);

          let base = undefined;
          try {
            if (paintProperty && paintProperty.base){
              base = paintProperty.base;
              delete paintProperty.base;
            }
          } catch (error) {
            // throw new Error(String(error));
          }

          if (typeof paintProperty !== "boolean" && !paintProperty){
            value = {"basic": (<any>defaults)[type]};

          } else if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)))){
            value = {"basic": paintProperty};
          } else if (paintProperty && paintProperty[0] === 'get') {
            value = {"basic": paintProperty};
          } else if (paintProperty && paintProperty[0] === 'step') {
            value = {"step": paintProperty};
          } else if (paintProperty && paintProperty[0] === 'case') {
            try {
              value = {"case": paintProperty,"field":paintProperty[1][1][1]};
            } catch (error) {
              value = {"case": paintProperty};
            }

          } else if (paintProperty && paintProperty[0] === 'match') {
            value = {"match": paintProperty};
          } else if (paintProperty && paintProperty[0] === 'interpolate') {
            let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
            
            if (propertyObject?.inputs.length === 1){
              if ( (["string", "number", "boolean"].includes(typeof paintProperty)) ) {
                value = {"basic": paintProperty};
              }else {
                try {
                  value = {"case": paintProperty,"field":paintProperty[1][1][1]};
                } catch (error) {
                  value = {"case": paintProperty};
                }
                
              }
              
            }else {
              value = {"interpolate": propertyObject.stops, "field":propertyObject.fieldName};
              if (base){
                (<any>value)["base"] = base;
              }
            }
            
          } else if (paintProperty && typeof paintProperty === 'object' && Object.keys(paintProperty)[0] === 'stops') {
            value = {"interpolate": paintProperty.stops, "field":"zoom"};
            if (base){
              (<any>value)["base"] = base;
            }
          } else {
            value = {"basic": paintProperty};
          }
        } else {
          paintProperty = this.mapObj.getLayoutProperty(layerId, type);
          
          let base = undefined;
          try {
            if (paintProperty && paintProperty.base){
              base = paintProperty.base;
              delete paintProperty.base;
            }
          } catch (error) {
            // throw new Error(String(error));
          }

          if (typeof paintProperty !== "boolean" && !paintProperty){
            value = {"basic": (<any>defaults)[type]};
          } else if (paintProperty && ((["string", "number", "boolean"].includes(typeof paintProperty)))){
            value = {"basic": paintProperty};
          } else if (paintProperty && paintProperty[0] === 'get') {
            value = {"basic": paintProperty};
          } else if (paintProperty && paintProperty[0] === 'case') {
            try {
              value = {"case": paintProperty,"field":paintProperty[1][1][1]};
            } catch (error) {
              value = {"case": paintProperty};
            }
              
          } else if (paintProperty && paintProperty[0] === 'match') {
            value = {"match": paintProperty};
          } else if (paintProperty && paintProperty[0] === 'interpolate') {
            let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
            
            if (propertyObject?.inputs.length === 1){
              if ((["string", "number", "boolean"].includes(typeof paintProperty))) {
                value = {"basic": paintProperty};
              }else {
                try {
                  value = {"case": paintProperty,"field":paintProperty[1][1][1]};
                } catch (error) {
                  value = {"case": paintProperty};
                } 
              }
              
            }else {
              value = {"interpolate": propertyObject.stops, "field":propertyObject.fieldName};
              if (base){
                (<any>value)["base"] = base;
              }
            }
            
          } else if (paintProperty && typeof paintProperty === 'object' && Object.keys(paintProperty)[0] === 'stops') {
            value = {"interpolate": paintProperty.stops, "field":"zoom"};
            if (base){
              (<any>value)["base"] = base;
            }
          } else {
            value = {"basic": paintProperty};
          }
        }
      }
      return value;
    } catch (error) {
      // throw new Error(String(error));
    }
  };

  // moveLayer = (newIndex: number) => {
  //   newIndex += 1;

  //   const { layers } = this.mapObj.getStyle();

  //   if (newIndex < 0) newIndex = 0;
  //   // if (newIndex >= layers.length) newIndex = layers.length;

  //   if (newIndex >= layers.length) {
  //     this.mapObj.moveLayer(this.layerId);
  //   } else {
  //     this.mapObj.moveLayer(
  //       this.layerId,
  //       this.mapObj.getStyle().layers[newIndex].id
  //     );
  //   }
  // };

  makeExpression = (value: any) => {
    // TODO
    const fieldSpec = {
      expression: {
        interpolated: true,
        parameters: ['zoom', 'feature', 'feature-state'],
      },
    };

    let expression;
    let type;

    if (typeof value === 'object' && 'stops' in value) {
      type = 'interpolate';
      // TODO
      expression = styleFunction.convertFunction(value, <any>fieldSpec);
    } else if (Utils.isArray(value) && value.includes('interpolate')) {
      type = 'interpolate';
      expression = value;
    } else if (this.isIdentityProperty(value)) {
      type = 'identity';
      expression = ['get', value.property];
    } else {
      type = 'literal';
      expression = ['literal', value];
    }

    return [type, expression];
  };

  isIdentityProperty = (value: {type: string}) => {
    return (
      typeof value === 'object' &&
      value.type === 'identity' &&
      value.hasOwnProperty('property')
    );
  };

  prepareNewStyle = (styleValue: any, isOpacity: boolean= false, subLayer: boolean =false) => {
    const selectColor = this.mapObj.mapSettings.general_settings.selection_color;
    let fillHoverColor: string | string[];
    const hoverColor = this.mapObj.mapSettings.general_settings.highlight_color;
    
    let layer = <LayerSpecification>this.mapObj.getLayer(this.layerId);
    const [type, expression] = this.makeExpression(styleValue);

    let newExpression = <any[]>expression;
    try {
        if (type === 'interpolate') {
          for (let i = 4; i < newExpression.length; i += 2) {
            if (layer.type === 'fill' || layer.type === 'fill-extrusion') {
              fillHoverColor = Utils.hexToRgba(hoverColor);
              if (!(Utils.isArray(newExpression[i]) || Utils.isObject(newExpression[i]))) 
                {
                  fillHoverColor = Utils.hexToRgba(Utils.colorNameToRGBA(newExpression[i]));
                }
             
              fillHoverColor = fillHoverColor.replace(/\s/g, '');
             
              if (fillHoverColor.includes('rgba')) {
                const SplitedColor = fillHoverColor.slice(5, fillHoverColor.length - 1).split(',');
                
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(SplitedColor[1])},${Number(SplitedColor[2])},${Number(SplitedColor[3]) === 0 ? 0 : Number(SplitedColor[3]) - 0.1})`;
              } else if (fillHoverColor.includes('rgb')) {
                const SplitedColor = fillHoverColor.slice(4, fillHoverColor.length - 1).split(',');

                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(SplitedColor[1])},${Number(SplitedColor[2])},0.5)`;
              }
              if (isOpacity){
                if (isNaN(newExpression[i])){
                  newExpression[i] = [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    fillHoverColor,
                    newExpression[i],
                  ];
                }else {
                  newExpression[i] = [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    newExpression[i]-0.1,
                    newExpression[i],
                  ];
                }
                
              }else {
                newExpression[i] = [
                  'case',
                  ['boolean', ['feature-state', 'select'], false],
                  selectColor,
                  ['boolean', ['feature-state', 'highlight'], false],
                  fillHoverColor,
                  newExpression[i],
                ];
              }
             
            } else {
              if (isOpacity){
                if (isNaN(newExpression[i])){
                  newExpression[i] = [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    newExpression[i],
                    newExpression[i],
                  ];
                }else {
                  newExpression[i] = [
                    'case',
                    ['boolean', ['feature-state', 'highlight'], false],
                    newExpression[i]-0.1,
                    newExpression[i],
                  ];
                }
                
              }else {
                newExpression[i] = [
                  'case',
                  ['boolean', ['feature-state', 'select'], false],
                  selectColor,
                  ['boolean', ['feature-state', 'highlight'], false],
                  hoverColor,
                  newExpression[i],
                ];
              }
            }
          }
        } else {
          let selfValue = styleValue;
          if (type === 'identity') {
            selfValue = <DataDrivenPropertyValueSpecification<string>>(
              newExpression
            );
          }
          if (layer.type === 'fill' || layer.type === 'fill-extrusion') {
            fillHoverColor = Utils.hexToRgba(hoverColor);
            if (!(Utils.isArray(selfValue) || Utils.isObject(selfValue))) {
              
              fillHoverColor = Utils.hexToRgba(Utils.colorNameToRGBA(selfValue));
          
              fillHoverColor = fillHoverColor.replace(/\s/g, '');
              
              if (fillHoverColor.includes('rgba')) {
                const SplitedColor = fillHoverColor
                  .slice(5, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},${Number(SplitedColor[3]) === 0? 0: Number(SplitedColor[3]) - 0.1})`;
              } else if (fillHoverColor.includes('rgb')) {
                const SplitedColor = fillHoverColor
                  .slice(4, fillHoverColor.length - 1)
                  .split(',');
                fillHoverColor = `rgba(${Number(SplitedColor[0])},${Number(
                  SplitedColor[1]
                )},${Number(SplitedColor[2])},0.5)`;
              }
            } else if ((<string[]>selfValue)[0] === 'match') {
              fillHoverColor = [...(<string[]>selfValue)];
              if (fillHoverColor.length > 1) {
                for (
                  let index = 3;
                  index < fillHoverColor.length - 1;
                  index += 2
                ) {
                  let color = Utils.hexToRgba(fillHoverColor[index]);
                  color = color.replace(/\s/g, '');
                  if (color.includes('rgba')) {
                    const SplitedColor = color
                      .slice(5, color.length - 1)
                      .split(',');
                    color = `rgba(${Number(SplitedColor[0])},${Number(
                      SplitedColor[1]
                    )},${Number(SplitedColor[2])},${
                      Number(SplitedColor[3]) === 0? 0: Number(SplitedColor[3]) - 0.1
                    })`;
                  } else if (color.includes('rgb')) {
                    const SplitedColor = color
                      .slice(4, color.length - 1)
                      .split(',');
                    color = `rgba(${Number(SplitedColor[0])},${Number(
                      SplitedColor[1]
                    )},${Number(SplitedColor[2])},0.5)`;
                  }
                  fillHoverColor[index] = color;
                }

                let color = Utils.hexToRgba(
                  fillHoverColor[fillHoverColor.length - 1]
                );
                color = color.replace(/\s/g, '');
                if (color.includes('rgba')) {
                  const SplitedColor = color
                    .slice(5, color.length - 1)
                    .split(',');
                  color = `rgba(${Number(SplitedColor[0])},${Number(
                    SplitedColor[1]
                  )},${Number(SplitedColor[2])},${
                    Number(SplitedColor[3]) === 0? 0: Number(SplitedColor[3]) - 0.1
                  })`;
                } else if (color.includes('rgb')) {
                  const SplitedColor = color
                    .slice(4, color.length - 1)
                    .split(',');
                  color = `rgba(${Number(SplitedColor[0])},${Number(
                    SplitedColor[1]
                  )},${Number(SplitedColor[2])},0.5)`;
                }

                fillHoverColor[fillHoverColor.length - 1] = color;
              }
             
            }
            if (subLayer){
              if (isOpacity){
                newExpression = [
                  'case',
                  ['boolean', ['feature-state', 'highlight'], false],
                  <number>selfValue-0.1 || hoverColor,
                  <number>selfValue,
                ];
              }else {
                newExpression = [
                  'case',
                  ['boolean', ['feature-state', 'select'], false],
                  selectColor,
                  ['boolean', ['feature-state', 'highlight'], false],
                  hoverColor,
                  <ExpressionInputType>selfValue,
                ];
              }
            }else {
              if (isOpacity){
                newExpression = [
                  'case',
                  ['boolean', ['feature-state', 'highlight'], false],
                  <number>selfValue-0.1 || hoverColor,
                  <number>selfValue,
                ];
              }else {
                newExpression = [
                  'case',
                  ['boolean', ['feature-state', 'select'], false],
                  selectColor,
                  ['boolean', ['feature-state', 'highlight'], false],
                  <ExpressionInputType>fillHoverColor,
                  <ExpressionInputType>selfValue,
                ];
              }
            }
            
          } else {
            if (isOpacity){
              newExpression = [
                'case',
                ['boolean', ['feature-state', 'highlight'], false],
                <number>selfValue-0.1 || hoverColor,
                <number>selfValue,
              ];
            }else {
              newExpression = [
                'case',
                ['boolean', ['feature-state', 'select'], false],
                selectColor,
                ['boolean', ['feature-state', 'highlight'], false],
                hoverColor,
                <ExpressionInputType>selfValue,
              ];
            }
          }
        }
      
    } catch (error) {
      // throw new Error(String(error));
    }

    return newExpression;
  };

  

}
